/**
 * Create-loan-from-sizer, with a REVIEW step.
 *
 * POST /underwriting/new_loan/populate_loan_form parses an EV product sizer
 * (DSCR / GUC / MF / RTL by cell map, else the standardized named-range intake)
 * and returns a fully populated — but NOT persisted — loan object. Nothing is
 * written until the user submits the create form.
 *
 * That "parsed but unsaved" shape is exactly what makes a review step possible:
 * rather than silently overwriting the form with everything the parser found, we
 * show what was extracted and let the user choose which values to apply. A sizer
 * is a modeling tool, so its cells are often stale or aspirational — the operator
 * is the one who knows which numbers are real.
 *
 * Only top-level scalar fields are individually selectable. Nested collections
 * (subjectProperties, borrowers, …) are applied as a group — cherry-picking
 * inside them would produce a half-built object the create form can't validate.
 */
import { useCallback, useMemo, useState } from "react";
import { isArray, isEmpty, isNil } from "lodash";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import PrefillUploadCard from "./PrefillUploadCard";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";

// Keys the parser sets for its own bookkeeping / the backend sets on the object —
// not user-facing form values, so they're applied silently rather than listed.
const INTERNAL_KEYS = new Set([
  "_id",
  "createdFrom",
  "createdBy",
  "permittedUsers",
  "rolesPermittedUsers",
  "createDateTime",
  "updateDateTime",
]);

// Label a camelCase key without a hardcoded dictionary — the sizer's field set
// varies by product, so a fixed map would silently drop unknown fields.
const humanize = (key) =>
  key
    .replace(/([A-Z]+)/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const isScalar = (v) =>
  v !== null && typeof v !== "object" && typeof v !== "undefined";

// Format for display only — the raw value is what gets applied.
const display = (key, value) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (/amount|price|upb|balance|cost|budget|value|rent|fee/i.test(key)) {
      return formatDollar(value, String(value));
    }
    return String(value);
  }
  if (typeof value === "string" && /date/i.test(key)) {
    return formatDate(value, value);
  }
  return String(value);
};

const SizerImportProcess = ({ setFormData }) => {
  const { loading, populateLoanFormFromExcel } = useUnderwritingHook();

  const [parsed, setParsed] = useState(null); // the populated (unsaved) loan
  const [selected, setSelected] = useState({}); // key -> bool
  const [error, setError] = useState(null);

  const onUpload = useCallback(
    (files) => {
      if (!isArray(files) || isEmpty(files)) return;
      const raw = files[0];
      const file = raw instanceof File ? raw : raw?.file;
      if (!file || !(file instanceof File)) {
        setError("Invalid file — please try again.");
        return;
      }
      setError(null);
      populateLoanFormFromExcel({
        excel: file,
        onSuccessFn: (data) => {
          if (isNil(data) || isEmpty(data)) {
            setError("Nothing could be read from that sizer.");
            return;
          }
          setParsed(data);
          // Default to applying everything — the review step is there to let the
          // user UNCHECK what's stale, not to make them re-enter the whole sizer.
          const next = {};
          Object.entries(data).forEach(([k, v]) => {
            if (INTERNAL_KEYS.has(k) || isNil(v)) return;
            next[k] = true;
          });
          setSelected(next);
        },
        onFailFn: (err) =>
          setError(err?.message || "Could not read that sizer."),
      });
    },
    [populateLoanFormFromExcel],
  );

  // Split what the parser returned into individually-selectable scalars vs
  // collections that only make sense applied whole.
  const { scalars, groups } = useMemo(() => {
    const s = [];
    const g = [];
    Object.entries(parsed || {}).forEach(([key, value]) => {
      if (INTERNAL_KEYS.has(key) || isNil(value)) return;
      if (isScalar(value)) {
        s.push({ key, value });
      } else if (isArray(value) ? !isEmpty(value) : !isEmpty(value)) {
        g.push({
          key,
          value,
          count: isArray(value) ? value.length : Object.keys(value).length,
        });
      }
    });
    return { scalars: s, groups: g };
  }, [parsed]);

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );

  const toggle = useCallback(
    (key) => setSelected((p) => ({ ...p, [key]: !p[key] })),
    [],
  );

  const setAll = useCallback(
    (on) =>
      setSelected((p) =>
        Object.keys(p).reduce((a, k) => ({ ...a, [k]: on }), {}),
      ),
    [],
  );

  const handleApply = useCallback(() => {
    if (!parsed) return;
    // Apply only what's checked, plus the parser's internal keys (they carry the
    // backend's own bookkeeping and aren't the user's to opt out of).
    const applied = Object.entries(parsed).reduce((acc, [k, v]) => {
      if (INTERNAL_KEYS.has(k)) return { ...acc, [k]: v };
      return selected[k] ? { ...acc, [k]: v } : acc;
    }, {});
    setFormData(applied);
    setParsed(null);
  }, [parsed, selected, setFormData]);

  return (
    <Flx column g={1} sx={{ height: "100%" }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <PrefillUploadCard
        id="prefill-upload-loan-sizer"
        icon={<TableChartOutlined sx={{ fontSize: 26 }} />}
        title="Upload Loan Sizer"
        description="Extract data from an EV product sizer and prefill this form"
        accept=".xlsx,.xlsm,.xls"
        loading={loading}
        onFilesAdded={onUpload}
      />

      <Dialog
        open={!!parsed}
        onClose={() => setParsed(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          <Flx ac jb sx={{ gap: 2 }}>
            <Flx ac sx={{ gap: 1 }}>
              <TableChartOutlined sx={{ fontSize: 18, color: "#4338ca" }} />
              Review Extracted Sizer Data
            </Flx>
            <Chip
              size="small"
              label={`${selectedCount} selected`}
              sx={{ fontSize: 11, fontWeight: 600 }}
            />
          </Flx>
        </DialogTitle>
        <DialogContent dividers>
          <Txt sx={{ fontSize: 12.5, color: "#64748b", display: "block", mb: 2 }}>
            Uncheck anything the sizer got wrong or that's out of date. Only the
            checked values are applied to the form — nothing is saved until you
            submit it.
          </Txt>

          <Flx sx={{ gap: 1, mb: 2 }}>
            <Button variant="text" size="small" onClick={() => setAll(true)}>
              Select all
            </Button>
            <Button variant="text" size="small" onClick={() => setAll(false)}>
              Clear all
            </Button>
          </Flx>

          {scalars.length === 0 && groups.length === 0 && (
            <Txt sx={{ fontSize: 12.5, color: "#64748b" }}>
              The sizer parsed, but no usable fields came back.
            </Txt>
          )}

          {scalars.map(({ key, value }) => (
            <Flx
              key={key}
              ac
              jb
              sx={{
                gap: 2,
                py: 0.75,
                borderBottom: "1px solid #eef2f5",
              }}
            >
              <Flx ac sx={{ gap: 1, minWidth: 0 }}>
                <Checkbox
                  size="small"
                  checked={!!selected[key]}
                  onChange={() => toggle(key)}
                />
                <Txt sx={{ fontSize: 12.5, color: "#475569" }}>
                  {humanize(key)}
                </Txt>
              </Flx>
              <Txt
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#0f172a",
                  textAlign: "right",
                }}
              >
                {display(key, value)}
              </Txt>
            </Flx>
          ))}

          {groups.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Txt
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: "#94a3b8",
                  display: "block",
                  mb: 1,
                }}
              >
                COLLECTIONS (APPLIED AS A GROUP)
              </Txt>
              {groups.map(({ key, count }) => (
                <Flx
                  key={key}
                  ac
                  jb
                  sx={{ gap: 2, py: 0.75, borderBottom: "1px solid #eef2f5" }}
                >
                  <Flx ac sx={{ gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={!!selected[key]}
                      onChange={() => toggle(key)}
                    />
                    <Txt sx={{ fontSize: 12.5, color: "#475569" }}>
                      {humanize(key)}
                    </Txt>
                  </Flx>
                  <Chip
                    size="small"
                    label={`${count} item${count === 1 ? "" : "s"}`}
                    sx={{ fontSize: 11 }}
                  />
                </Flx>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setParsed(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={selectedCount === 0}
          >
            Apply {selectedCount} Field{selectedCount === 1 ? "" : "s"}
          </Button>
        </DialogActions>
      </Dialog>
    </Flx>
  );
};

export default SizerImportProcess;
